import { reuseSsrPayloadOnServer } from '~/utils/reuseSsrPayload';
import log from '~/utils/log';

/** `DoceboUserStatus` plus `unknown`, for when Docebo could not be reached. */
export type AcademyConsentStatus = 'active' | 'inactive' | 'unknown';

const ACADEMY_CONSENT_STATUS_KEY = 'academy:docebo-user-status';

/**
 * Resolves whether the current user has given the Academy consent.
 *
 * `dedupe: 'defer'` joins callers whose requests overlap; `getCachedData` lets one server render
 * share a single response between callers that do not, see {@link reuseSsrPayload}.
 */
export const useAcademyConsentStatus = () =>
    useAsyncData<AcademyConsentStatus>(
        ACADEMY_CONSENT_STATUS_KEY,
        async () => {
            const requestFetch = useRequestFetch();

            try {
                const response = await requestFetch<{ status: 'active' | 'inactive' }>(
                    '/api/academy/docebo/user-status'
                );

                return response.status;
            } catch (error) {
                log.error('[useAcademyConsentStatus] Failed to resolve the Docebo user status', error);

                return 'unknown';
            }
        },
        {
            dedupe: 'defer',
            getCachedData: reuseSsrPayloadOnServer,
            default: (): AcademyConsentStatus => 'unknown'
        }
    );

/** Keeps the interpretation of `unknown` in one place. */
export const isAcademyConsentGiven = (status: AcademyConsentStatus) => status === 'active';
